import { useQuery } from "@tanstack/react-query";
import { History, Inbox, KeyRound, Send, Smartphone } from "lucide-react";
import type { CursorPage, StatsOverview } from "@smsbridge/shared";
import { Card } from "../components/ui/Card";
import { EmptyState } from "../components/ui/EmptyState";
import { CountUp } from "../components/ui/CountUp";
import { QuotaCardsSkeleton, StatStripSkeleton } from "../components/ui/Skeletons";
import { PageHeader } from "../components/ui/PageHeader";
import { ProgressMeter } from "../components/ui/ProgressMeter";
import { LiveIndicator } from "../components/ui/LiveIndicator";
import { StatCard } from "../components/ui/StatCard";
import { StatusPill } from "../components/ui/StatusPill";
import { Table, type TableColumn } from "../components/ui/Table";
import { api, apiErrorMessage } from "../lib/api";
import { formatCount, formatDateTime } from "../lib/format";
import { DASHBOARD_POLL_MS, polling } from "../lib/polling";
import { useMinimumDuration } from "../lib/useMinimumDuration";
import { useAuthStore } from "../stores/auth-store";

interface MessageListItem {
  _id: string;
  direction: "SENT" | "RECEIVED";
  status: string;
  recipient: string | null;
  sender: string | null;
  body: string;
  createdAt: string;
}

const RECENT_COLUMNS: TableColumn<MessageListItem>[] = [
  {
    key: "direction",
    header: "",
    width: "44px",
    render: (row) =>
      row.direction === "SENT" ? (
        <Send className="size-4 text-text-muted" aria-label="Sent" />
      ) : (
        <Inbox className="size-4 text-text-muted" aria-label="Received" />
      ),
  },
  {
    key: "peer",
    header: "Contact",
    render: (row) => (
      <span className="font-medium text-text">{row.recipient ?? row.sender ?? "—"}</span>
    ),
  },
  {
    key: "body",
    header: "Message",
    render: (row) => (
      <span className="line-clamp-1 max-w-md text-text-muted">{row.body}</span>
    ),
  },
  {
    key: "status",
    header: "Status",
    width: "120px",
    render: (row) => <StatusPill status={row.status} />,
  },
  {
    key: "createdAt",
    header: "When",
    width: "160px",
    align: "right",
    render: (row) => (
      <span className="whitespace-nowrap text-text-muted">{formatDateTime(row.createdAt)}</span>
    ),
  },
];

export function DashboardPage() {
  const user = useAuthStore((s) => s.user);

  const statsQuery = useQuery({
    queryKey: ["stats", "overview"],
    queryFn: async () => (await api.get<StatsOverview>("/stats/overview")).data,
    ...polling(DASHBOARD_POLL_MS),
  });

  const messagesQuery = useQuery({
    queryKey: ["messages", { limit: 8 }],
    queryFn: async () =>
      (await api.get<CursorPage<MessageListItem>>("/messages", { params: { limit: 8 } })).data,
    ...polling(DASHBOARD_POLL_MS),
  });

  const messagesQueryLoading = useMinimumDuration(messagesQuery.isLoading);

  const stats = statsQuery.data;
  // Held briefly so a warm cache does not paint skeletons for three frames and tear them down.
  const statsLoading = useMinimumDuration(statsQuery.isLoading);

  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        sticky
        title={`Welcome back, ${user?.name ?? "there"}`}
        description="Here's what your SMS gateway has been doing."
        // Uses the header's existing actions slot, so nothing about the sticky header changes.
        actions={<LiveIndicator />}
      />

      {statsQuery.isError && (
        <p role="alert" className="rounded-control bg-danger-soft px-3 py-2 text-sm text-danger">
          {apiErrorMessage(statsQuery.error, "Could not load your stats.")}
        </p>
      )}

      <section className="overflow-hidden rounded-card border border-border bg-surface">
        {statsLoading ? (
          <>
            <QuotaCardsSkeleton />
            <StatStripSkeleton />
          </>
        ) : (
        <>
        <div className="grid gap-px bg-border sm:grid-cols-2">
          {/*
            gap-px over a border-coloured parent draws hairline dividers between cells without
            each cell owning a border — so there is no doubled 2px line where two cells meet,
            and the rule disappears cleanly when the grid collapses to one column.
          */}
          <div className="flex flex-col gap-3 bg-surface p-5">
            <div className="flex items-baseline justify-between gap-3">
              <p className="text-sm font-medium text-text">Today</p>
              <p className="tnum text-sm text-text-muted">
                {stats
                  ? `${formatCount(stats.today.used)} / ${formatCount(stats.today.limit)}`
                  : "—"}
              </p>
            </div>
            <ProgressMeter value={stats?.today.used ?? 0} max={stats?.today.limit ?? 1} />
            <p className="text-xs text-text-muted">Messages sent against your daily plan limit.</p>
          </div>

          <div className="flex flex-col gap-3 bg-surface p-5">
            <div className="flex items-baseline justify-between gap-3">
              <p className="text-sm font-medium text-text">Last 30 days</p>
              <p className="tnum text-sm text-text-muted">
                {stats
                  ? `${formatCount(stats.last30Days.used)} / ${formatCount(stats.last30Days.limit)}`
                  : "—"}
              </p>
            </div>
            <ProgressMeter value={stats?.last30Days.used ?? 0} max={stats?.last30Days.limit ?? 1} />
            <p className="text-xs text-text-muted">
              Messages sent against your monthly plan limit.
            </p>
          </div>
        </div>

        <div className="grid gap-px border-t border-border bg-border sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            label="SMS sent"
            value={stats ? <CountUp value={stats.totalSent} /> : "—"}
            icon={<Send className="size-5" aria-hidden />}
            tone="accent"
            className="rounded-none border-0"
          />
          <StatCard
            label="SMS received"
            value={stats ? <CountUp value={stats.totalReceived} /> : "—"}
            icon={<Inbox className="size-5" aria-hidden />}
            tone="neutral"
            className="rounded-none border-0"
          />
          <StatCard
            label="Devices"
            value={stats ? <CountUp value={stats.deviceCount} /> : "—"}
            icon={<Smartphone className="size-5" aria-hidden />}
            tone="neutral"
            className="rounded-none border-0"
          />
          <StatCard
            label="API keys active"
            value={stats ? <CountUp value={stats.activeApiKeyCount} /> : "—"}
            icon={<KeyRound className="size-5" aria-hidden />}
            tone="neutral"
            className="rounded-none border-0"
          />
        </div>
        </>
        )}
      </section>

      <section className="flex flex-col gap-4">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-semibold tracking-tight text-text">
            <History className="size-5 text-text-muted" aria-hidden />
            Recent messages
          </h2>
          <p className="mt-0.5 text-sm text-text-muted">The latest traffic across your devices.</p>
        </div>
        <Table
          columns={RECENT_COLUMNS}
          data={messagesQuery.data?.items ?? []}
          getRowId={(row) => row._id}
          loading={messagesQueryLoading}
          emptyState={
            <EmptyState
              icon={<Send className="size-6" aria-hidden />}
              title="No messages yet"
              description="Once a paired device sends or receives an SMS, it shows up here."
            />
          }
        />
      </section>
    </div>
  );
}
